import {getCurrentHtmlContentId} from "../utilities/general";
import {getSectionMarksFromSessionStorage, clearMarkObjectInSession} from "../account/annotations";

/**
 * Add a mark to session
 * @param mark
 * @private
 */
function addSectionMarkToCollectionInSessionStorage(mark) {
  let marks = getSectionMarksFromSessionStorage();
  marks.push(mark);

  sessionStorage.setItem('marks', JSON.stringify(marks));
}

/**
 * Adds a mark to the session storage using the provided key.
 *
 * @param {string} key - The key to be used for storing the mark in the session storage.
 * @param {*} mark - The mark to be stored in the session storage.
 * @return {void} - This method does not return anything.
 */
function addMarkToSessionStorage(key, mark) {
  sessionStorage.setItem(key, JSON.stringify(mark));
}

/**
 * Pushes the current state or marks (session storage marks variable) into the marks-history object.
 *
 * @return {void}
 */
function pushToMarkHistory() {
  const marks = JSON.parse(sessionStorage.getItem('marks'));
  let history = JSON.parse(sessionStorage.getItem('marks-history'));

  // initialize the history variable if necessary
  if(!history) {
    history = [];
  }

  // store the entire array of marks data on the section, if there's been a change
  if((history.length === 0) || (marks !== history[history.length - 1])) {
    history.push(marks);
  }

  sessionStorage.setItem('marks-history', JSON.stringify(history));

  // keep a history of no more than 5 previous states
  if(history.length > 5) {
    removeOldestFromMarksHistory();
  }
}

/**
 * Returns true if the have reach the number of marks history entries we want to keep in session.
 * @param maxToSTore
 * @returns {boolean}
 */
function isMarksHistoryFull(maxToSTore = 5) {
  let history = JSON.parse(sessionStorage.getItem('marks-history'));
  return history.length >= maxToSTore;
}

function popFromMarksHistory() {
  let history = JSON.parse(sessionStorage.getItem('marks-history')) ?? [];
  let previousState;

  if(history && history.length > 0) {
    previousState = history.pop();
    sessionStorage.setItem('marks-history', JSON.stringify(history));

    return JSON.stringify(previousState);
  }

  return JSON.stringify([]);
}

/**
 * Removes the oldest element from the mark history if there [undoMax] or more entries in the array.
 *
 * @returns {string|boolean}
 */
function removeOldestFromMarksHistory() {
  let history = JSON.parse(sessionStorage.getItem('marks-history'));
  let oldest;

    // remove oldest entry
    oldest = history.shift();

    let updated = JSON.stringify(history);
    sessionStorage.setItem('marks-history', updated);

    return JSON.stringify(oldest);
}

/**
 * Find a mark object in session storage by mark ID
 * @param allMarks
 * @param markId
 * @returns {*}
 * @private
 */
function findMarkInSectionCollectionInSessionStorage(allMarks, markId) {
  // guar clause - if collection null, return null
  if(!allMarks) {
    return null;
  }

  return allMarks.find(
    (mark) => parseInt(mark.id) === parseInt(markId)
  );
}

/**
 * Update a mark in session Storage.
 * @param allMarks
 * @param markData
 * @private
 */
function updateMarkInSectionCollectionInSessionStorage(allMarks, markData) {
  let marks = sectionMarks(allMarks);
  const index = indexOfMarkInSession(allMarks, markData);
  marks[index] = markData;
  sessionStorage.setItem('marks', JSON.stringify(marks));
}

/**
 * Returns the index of the mark in the session array
 * @param allMarks
 * @param mark
 * @returns {*}
 * @private
 */
function indexOfMarkInSession(allMarks, mark) {
  let id = mark.id;
  return sectionMarks(allMarks).findIndex((mark) => parseInt(mark.id) === parseInt(id));
}

/**
 * Find a mark in the marks session Storage array and remove it
 * returns the filtered array
 * @param allMarks
 * @param markId
 * @returns {*[]}
 * @private
 */
function removeMarkFromSectionCollectionInSessionStorage(allMarks, markId) {
  let filteredMarks = [];
  if (markId) {
    // filter the mark to delete; filteredMarks will contain all marks except the one we are deleting
    filteredMarks = allMarks.filter((item) => {
      // if there was an error processing a new mark, it will have already been removed from storage;
      // thus the optional chaining
      return item.id !== findMarkInSectionCollectionInSessionStorage(allMarks, markId)?.id;
    });

    // overwrite the marks array with the filtered array
    sessionStorage.setItem('marks', JSON.stringify(filteredMarks));
  }

  return filteredMarks;
}

/**
 *
 * Filters and sorts the marks in the session object 'marks'.
 *
 * Return marks with the given contentId and sectionId, sorted by largest start range offset, in essence sorting
 * marks putting the furthest mark down the page first.
 * If no content / section ids are passed in, fetch them using helper methods
 * @param allMarks
 * @param htmlContentId
 * @returns {*}
 * @private
 */
function sectionMarks(allMarks, htmlContentId = null) {
  let filteredAndSorted = [];

  try {
    // set contentId, sectionId if null
    htmlContentId = (htmlContentId === null) ? getCurrentHtmlContentId() : htmlContentId;

    let marks
      = allMarks
      .filter(
        mark => mark.htmlContentId === htmlContentId
      );
    filteredAndSorted = marks.sort((a, b) => b.rangeOffsets.start - a.rangeOffsets.start);

    if (!filteredAndSorted) {
      // TODO: do we need this?
      // clearMarkObjectInSession();
      console.log(`filteredAndSorted should not be empty but it is...`)
    }
  }catch(error) {
    console.log(error.message);
  }

  return filteredAndSorted;
}

/**
 * Get the selected highlight color from session storage.
 * @returns {string}
 * @private
 */
function getHighlightColor () {
  return sessionStorage.getItem('highlight-color')
    ?? sessionStorage.getItem('default-highlight-color');
}

export {
  addMarkToSessionStorage, addSectionMarkToCollectionInSessionStorage,
  findMarkInSectionCollectionInSessionStorage, getHighlightColor,
  getSectionMarksFromSessionStorage, isMarksHistoryFull, pushToMarkHistory, removeOldestFromMarksHistory,
  popFromMarksHistory,removeMarkFromSectionCollectionInSessionStorage,
  sectionMarks, updateMarkInSectionCollectionInSessionStorage
}
